import { useState } from 'react';

import { promptText } from '../../../lib/prompt-dialog.js';
import { BLEND_MODES, BLEND_SPACES, usePaint, type BlendSpaceName } from '../../../state/paint.js';
import { IconButton, NumberField, SelectField } from '../../ui/controls.js';
import { Icon } from '../../ui/icons.js';
import './paint.css';

/**
 * Paint layers (top first) floating over the model viewport while texture
 * painting: visibility, active layer, blend mode, opacity, order.
 */
export function PaintLayersPanel() {
  const active = usePaint((s) => s.active);
  const layers = usePaint((s) => s.layers);
  const current = usePaint((s) => s.activeLayer);
  const warning = usePaint((s) => s.warning);
  const blendSpace = usePaint((s) => s.blendSpace);
  const [open, setOpen] = useState(true);
  if (!active) return null;
  const p = usePaint.getState();
  const layer = layers[current];
  const rows = layers.map((l, i) => ({ l, i })).reverse();
  return (
    <div className="paint-layers" onPointerDown={(e) => e.stopPropagation()}>
      <header className="paint-layers-head">
        <button type="button" className="card-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
          <Icon name="layers" size={13} />
          <span>Layers</span>
        </button>
        <span className="tb-spacer" />
        <IconButton icon="plus" title="Add layer" size={13} onClick={() => p.layer('add')} />
        <IconButton icon="copy" title="Duplicate layer" size={13} onClick={() => p.layer('duplicate', { index: current })} />
        <IconButton icon="trash" title="Delete layer" size={13} disabled={layers.length <= 1} onClick={() => p.layer('remove', { index: current })} />
      </header>
      {open && (
        <>
          <ul className="paint-layer-list" role="listbox" aria-label="Paint layers">
            {rows.map(({ l, i }) => (
              <li
                key={i}
                role="option"
                aria-selected={i === current}
                className={`paint-layer${i === current ? ' is-active' : ''}${l.hidden ? ' is-hidden' : ''}`}
                onClick={() => p.layer('select', { index: i })}
                onDoubleClick={async () => {
                  const name = await promptText('Layer name', l.name);
                  if (name && name !== l.name) p.layer('rename', { index: i, value: name });
                }}
                title={l.image ? `${l.name} — ${l.image}` : `${l.name} (not saved yet)`}
              >
                <button
                  type="button"
                  className="ibtn"
                  title={l.hidden ? 'Show layer' : 'Hide layer'}
                  onClick={(e) => {
                    e.stopPropagation();
                    p.layer('hidden', { index: i, value: !l.hidden });
                  }}
                >
                  <Icon name={l.hidden ? 'eyeOff' : 'eye'} size={13} />
                </button>
                <span className="paint-layer-name">{l.name}</span>
                <span className="paint-layer-meta muted">
                  {l.blend !== 'normal' ? BLEND_MODES.find((b) => b.value === l.blend)?.label : ''}
                  {l.opacity < 1 ? ` ${Math.round(l.opacity * 100)}%` : ''}
                </span>
              </li>
            ))}
          </ul>
          {layer && (
            <div className="paint-layer-props">
              <SelectField value={layer.blend} options={BLEND_MODES} onChange={(v) => p.layer('blend', { index: current, value: v })} />
              <NumberField
                label="Opacity"
                value={layer.opacity}
                step={0.05}
                min={0}
                max={1}
                onChange={(v) => p.layer('opacity', { index: current, value: v })}
              />
              <div className="paint-actions">
                <IconButton icon="arrowUp" title="Move layer up" size={13} disabled={current >= layers.length - 1} onClick={() => p.layer('reorder', { index: current, to: current + 1 })} />
                <IconButton icon="arrowDown" title="Move layer down" size={13} disabled={current <= 0} onClick={() => p.layer('reorder', { index: current, to: current - 1 })} />
                <button type="button" className="ghost small" disabled={current <= 0} title="Merge into the layer below" onClick={() => p.layer('mergeDown', { index: current })}>
                  Merge down
                </button>
              </div>
            </div>
          )}
          <label className="paint-row" title={BLEND_SPACES.find((b) => b.value === blendSpace)?.title}>
            <span>Blend in</span>
            <SelectField
              value={blendSpace}
              options={BLEND_SPACES.map(({ value, label }) => ({ value, label }))}
              onChange={(v) => p.setBlendSpace(v as BlendSpaceName)}
            />
          </label>
          {warning && <p className="paint-layers-warning">{warning}</p>}
        </>
      )}
    </div>
  );
}
